import {
  Button,
  Flex,
  Box,
  Text,
  useToast,
  Icon,
  IconButton,
  Alert,
  AlertTitle,
  AlertDescription,
  Circle,
  VStack,
  HStack,
  Divider,
  Spinner,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { FaArrowLeft, FaArrowRight, FaCheck, FaShieldAlt, FaIdCard, FaTimes, FaUser } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { API, graphqlOperation, Auth } from "aws-amplify";
import { IoIosAlert } from "react-icons/io";
import { updateBookingSecurityItem } from "../../../graphql/mutations";
import { getBooking } from "../../../graphql/queries";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { nextIncompleteStep } from "../stepFlow";

export const GuestVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [guestIdSecurityItemId, setGuestIdSecurityItemId] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [showIframe, setShowIframe] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const toastShown = useRef(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // POLLING SYSTEM - Robust implementation with history tracking
  // Autohost can send premature responses early, so we track response history
  // and only trust the result when the LAST 4 polls are all "approved"
  // ═══════════════════════════════════════════════════════════════════════════
  const POLL_CONFIG = { 
    interval: 5000,               // 5 seconds between polls
    maxPolls: 120,                // 10 minutes max (fallback)
    totalPolls: 6,                // Do exactly 6 polls
    requiredApprovals: 4,         // Last 4 of 6 must be "approved" to force update
  };
  
  // All polling state in a single ref to avoid stale closures
  const pollingRef = useRef({
    intervalId: null,
    count: 0,
    isActive: false,
    isMounted: true,
    // Response history tracking (stores last N actions)
    responseHistory: [],
    // Track when portal is completed (user clicked Finish)
    portalCompleted: false,
    postCompletionCount: 0, // Count polls AFTER portal completion
  });
  
  // UI state (triggers re-renders)
  const [pollState, setPollState] = useState({
    isPolling: false,
    pollCount: 0,
    showModal: false,
    result: null,
    confirmations: 0, // How many of the last 4 are approved
  });

  const storedState = JSON.parse(localStorage.getItem("onboardingState")) || {};
  const {
    guest,
    booking,
    room,
    storedStripeId,
    manager: locationManager,
    accessTimes: locationAccessTimes,
  } = location.state || storedState;

  // A hop through a screen that doesn't carry manager/accessTimes must not
  // lose them (and must never clobber the stored copy with undefined) —
  // manager gates extended verification, and the localStorage write below
  // is the fallback every onboarding screen reads when state is missing.
  const manager = locationManager || storedState?.manager;
  const accessTimes = locationAccessTimes || storedState?.accessTimes;

  // Check if extended verification is enabled
  const hasOwnerSecretArn = manager?.accountMetas?.superService?.secretArn;
  const hasRoomAutohostId = room?.accountMetas?.autohostId;
  const isExtendedVerification = hasOwnerSecretArn && hasRoomAutohostId;


  // ✅ Store the latest state in `localStorage`
  useEffect(() => {
    if (guest && booking && room && storedStripeId) {
      localStorage.setItem(
        "onboardingState",
        JSON.stringify({ guest, booking, room, storedStripeId, manager, accessTimes })
      );
    }
  }, [guest, booking, room, storedStripeId, manager]);

  // Extract GUEST_ID from booking or URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const completed = params.get("completed") === "true";
    const bookingSecurityItemId = params.get("bookingSecurityItemId");

    if (booking) {
      const guestSecurityItem = booking.securityItems.items.find(
        (item) => item.type === "GUEST_ID"
      );

      if (guestSecurityItem) {
        setGuestIdSecurityItemId(guestSecurityItem.id);
        setIsVerified(guestSecurityItem.isComplete);
      }
    } else if (bookingSecurityItemId) {
      setGuestIdSecurityItemId(bookingSecurityItemId);
    }

    // If redirected with completed=true, mark as complete
    if (completed && !toastShown.current) {
      toastShown.current = true;
      handleVerificationCompletion(bookingSecurityItemId);
    }
  }, [location, booking]);

  // Track mounted state for cleanup
  useEffect(() => {
    pollingRef.current.isMounted = true;
    return () => {
      pollingRef.current.isMounted = false;
    };
  }, []);

  // Stable API call function - won't change between renders
  const pollVerificationStatus = useCallback(async (bookingId) => {
    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();
      const endpoint = `https://api.linkbase.ca/security/id_verify/poll/${bookingId}`;
      
      console.log("📡 [Polling] Endpoint:", endpoint);
      
      const { data } = await axios.post(endpoint, {}, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_LINKBASE_API_KEY,
        },
      });
      
      console.log("📊 [Polling] Response:", data);
      return data;
    } catch (error) {
      console.error("⚠️ [Polling] Failed:", error?.message);
      return null;
    }
  }, []);

  // Force update backend status after 6 consecutive confirmations
  const forceUpdateVerificationStatus = useCallback(async (bookingId, action) => {
    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();
      const endpoint = `https://api.linkbase.ca/security/id_verify/approve/${bookingId}`;
      
      console.log("🔒 [Force Update] Endpoint:", endpoint);
      console.log("🔒 [Force Update] Action:", action);
      
      const { data } = await axios.post(endpoint, { action }, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_LINKBASE_API_KEY,
        },
      });
      
      console.log("✅ [Force Update] Response:", data);
      return data;
    } catch (error) {
      console.error("❌ [Force Update] Failed:", error?.message);
      return null;
    }
  }, []);

  // Stop polling - stable reference
  const stopPolling = useCallback(() => {
    const ref = pollingRef.current;
    if (ref.intervalId) {
      console.log("🛑 [Polling] Stopping");
      clearInterval(ref.intervalId);
      ref.intervalId = null;
      ref.isActive = false;
      setPollState(prev => ({ ...prev, isPolling: false }));
    }
  }, []);

  // Mark portal as completed (called when we receive guestportal-completed message)
  const markPortalCompleted = useCallback(() => {
    const ref = pollingRef.current;
    if (!ref.portalCompleted) {
      console.log("🏁 [Polling] Portal completed! Starting 6-poll countdown...");
      ref.portalCompleted = true;
      ref.postCompletionCount = 0;
      ref.responseHistory = []; // Reset history - only care about polls AFTER completion
    }
  }, []);

  // Start polling - runs in background, only makes decisions AFTER portal completion
  const startPolling = useCallback((bookingId) => {
    const ref = pollingRef.current;
    
    // Guard: prevent duplicate polling
    if (ref.isActive || ref.intervalId) {
      console.log("⚠️ [Polling] Already active, skipping start");
      return;
    }
    
    console.log("🔄 [Polling] Starting background polling...");
    console.log("⏳ [Polling] Waiting for portal completion before making decisions");
    
    ref.isActive = true;
    ref.count = 0;
    ref.portalCompleted = false;
    ref.postCompletionCount = 0;
    ref.responseHistory = [];
    setPollState(prev => ({ ...prev, isPolling: true, pollCount: 0, confirmations: 0 }));

    const poll = async () => {
      // Guard: check if still mounted and active
      if (!ref.isMounted || !ref.isActive) {
        stopPolling();
        return;
      }
      
      ref.count += 1;
      const currentCount = ref.count;
      
      const response = await pollVerificationStatus(bookingId);
      
      // Guard: check again after async operation
      if (!ref.isMounted || !ref.isActive) return;

      // Only track polls AFTER portal completion
      if (ref.portalCompleted) {
        ref.postCompletionCount += 1;
        const postCount = ref.postCompletionCount;
        
        console.log(`🔄 [Polling] Post-completion poll #${postCount}/${POLL_CONFIG.totalPolls}`);
        
        // Update UI state with post-completion count
        if (ref.isMounted) {
          setPollState(prev => ({ ...prev, pollCount: postCount }));
        }

        if (!response) {
          console.warn("⚠️ [Polling] No response - adding null to history");
          ref.responseHistory.push({ action: null, isFinalResult: false });
        } else {
          const { action } = response;
          const guestportalCompleted = response.data?.verificationStatus?.guestportal_completed === true;
          const isFinalResult = action && action !== "pending" && guestportalCompleted;

          ref.responseHistory.push({ action, isFinalResult, guestportalCompleted, response });
          
          console.log(`📊 [Polling] Poll #${postCount}: action="${action}", final=${isFinalResult}`);
        }

        console.log(`📊 [Polling] History: [${ref.responseHistory.map(r => r?.action || 'null').join(', ')}]`);

        // After exactly 6 polls POST-COMPLETION, make final decision
        if (postCount === POLL_CONFIG.totalPolls) {
          console.log(`🏁 [Polling] All ${POLL_CONFIG.totalPolls} post-completion polls done - making decision`);
          
          stopPolling();
          
          // Get the last 4 responses
          const last4 = ref.responseHistory.slice(-POLL_CONFIG.requiredApprovals);
          const approvedCount = last4.filter(r => r?.action === "approved" && r?.isFinalResult).length;
          const lastResponse = ref.responseHistory[ref.responseHistory.length - 1]?.response;
          
          console.log(`🔍 [Polling] Last 4 results: [${last4.map(r => r?.action || 'null').join(', ')}]`);
          console.log(`🔍 [Polling] Approved count: ${approvedCount}/${POLL_CONFIG.requiredApprovals}`);
          
          // Update UI with final count
          if (ref.isMounted) {
            setPollState(prev => ({ ...prev, confirmations: approvedCount }));
          }

          // If last 4 are ALL approved → force update
          if (approvedCount === POLL_CONFIG.requiredApprovals) {
            console.log(`✅ [Polling] Last 4 are ALL approved! Force updating...`);
            
            await forceUpdateVerificationStatus(bookingId, "approved");
            
            setIsVerified(true);
            
            if (ref.isMounted) {
              setPollState(prev => ({
                ...prev,
                isPolling: false,
                result: { ...lastResponse?.data, action: "approved" },
                confirmations: POLL_CONFIG.requiredApprovals,
              }));
            }
          } else {
            // Not all approved - show the most recent final result
            const lastFinal = [...ref.responseHistory].reverse().find(r => r?.isFinalResult);
            const finalAction = lastFinal?.action || "rejected";
            
            console.log(`⚠️ [Polling] Last 4 NOT all approved (${approvedCount}/4). Final action: ${finalAction}`);
            
            setIsVerified(finalAction === "approved");
            
            if (ref.isMounted) {
              setPollState(prev => ({
                ...prev,
                isPolling: false,
                result: { ...lastResponse?.data, action: finalAction },
                confirmations: approvedCount,
              }));
            }
          }
          return;
        }
      } else {
        // Before portal completion - just log that we're waiting
        console.log(`⏳ [Polling] Background poll #${currentCount} - waiting for portal completion...`);
      }

      // Timeout check (safety fallback)
      if (currentCount >= POLL_CONFIG.maxPolls) {
        console.warn("⏰ [Polling] Timeout (10min). Stopping.");
        stopPolling();
      }
    };

    // Run first poll immediately, then set interval
    poll();
    ref.intervalId = setInterval(poll, POLL_CONFIG.interval);
    
  }, [pollVerificationStatus, stopPolling, forceUpdateVerificationStatus]);

  // Add beforeunload warning when processing modal is shown
  useEffect(() => {
    if (!pollState.showModal) return;
    
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Your verification is being processed. Are you sure you want to leave?';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    console.log("⚠️ [Polling] Browser close warning enabled");

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      console.log("✅ [Polling] Browser close warning disabled");
    };
  }, [pollState.showModal]);

  // Start polling when iframe loads (extended verification only)
  useEffect(() => {
    if (!showIframe || !isExtendedVerification || !booking?.id) return;
    if (pollingRef.current.isActive) return; // Already polling
    
    console.log("🎬 [Polling] Iframe loaded - Starting in 3s");
    
    // Set pending verification flag
    localStorage.setItem("pendingVerification", JSON.stringify({
      bookingId: booking.id,
      timestamp: Date.now()
    }));

    // Start polling after delay
    const timeoutId = setTimeout(() => {
      if (pollingRef.current.isMounted) {
        startPolling(booking.id);
      }
    }, 3000);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      stopPolling();
    };
  }, [showIframe, isExtendedVerification, booking?.id, startPolling, stopPolling]);

  // Listen for postMessage events from Autohost iframe
  useEffect(() => {
    const handleMessage = (m) => {
      // Quick exit for non-string data
      if (!m?.data || typeof m.data !== 'string') return;
      
      // Only try parsing JSON that looks like our format
      if (!m.data.includes('guestportal')) return;
      
      try {
        const data = JSON.parse(m.data);
        if (data.type !== 'guestportal-completed') return;
        
        console.log("📬 [PostMessage] guestportal-completed received - USER CLICKED FINISH");
        
        if (isExtendedVerification) {
          // Mark portal as completed - this starts the 6-poll countdown
          markPortalCompleted();
          
          // Show blocking modal
          console.log("🔒 [PostMessage] Showing processing modal");
          setPollState(prev => ({ ...prev, showModal: true, pollCount: 0 }));
        } else {
          // Non-extended: simple flow
          setIsVerified(true);
          setTimeout(() => {
            pollVerificationStatus(booking?.id);
          }, 7000);
        }

        // Update localStorage
        if (booking?.id) {
          localStorage.setItem("pendingVerification", JSON.stringify({
            bookingId: booking.id,
            timestamp: Date.now()
          }));
        }

        if (booking && guestIdSecurityItemId) {
          const updatedBooking = {
            ...booking,
            securityItems: {
              ...booking.securityItems,
              items: booking.securityItems.items.map((item) =>
                item.id === guestIdSecurityItemId ? { ...item, isComplete: true } : item
              ),
            },
          };
          
          localStorage.setItem(
            "onboardingState",
            JSON.stringify({ guest, booking: updatedBooking, room, storedStripeId, manager, accessTimes })
          );
        }
      } catch {
        // Not our message format - ignore silently
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [booking, guestIdSecurityItemId, guest, room, storedStripeId, manager, isExtendedVerification, pollVerificationStatus, markPortalCompleted]);

  const checkVerificationStatus = async () => {
    if (!booking?.id || !guestIdSecurityItemId) return;

    setCheckingStatus(true);

    try {
      const result = await API.graphql(
        graphqlOperation(getBooking, { id: booking.id })
      );

      const updatedBooking = result.data.getBooking;
      const guestSecurityItem = updatedBooking.securityItems.items.find(
        (item) => item.type === "GUEST_ID"
      );

      if (guestSecurityItem && guestSecurityItem.isComplete && !isVerified) {
        setIsVerified(true);
        
        const updatedBookingData = {
          ...booking,
          securityItems: updatedBooking.securityItems,
        };
        
        localStorage.setItem(
          "onboardingState",
          JSON.stringify({ guest, booking: updatedBookingData, room, storedStripeId, manager, accessTimes })
        );

        toast({
          title: "Verification Complete!",
          description: "Your ID has been successfully verified.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
      toast({
        title: "Error",
        description: "Failed to check verification status.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleVerificationCompletion = async (securityItemId) => {
    if (!securityItemId) return;

    setLoading(true);

    try {
      await API.graphql(
        graphqlOperation(updateBookingSecurityItem, { 
          input: { id: securityItemId, isComplete: true }
        })
      );

      const updatedBooking = {
        ...booking,
        securityItems: {
          ...booking.securityItems,
          items: booking.securityItems.items.map((item) =>
            item.id === securityItemId ? { ...item, isComplete: true } : item
          ),
        },
      };

      localStorage.setItem(
        "onboardingState",
        JSON.stringify({ guest, booking: updatedBooking, room, storedStripeId, manager, accessTimes })
      );

      setIsVerified(true);
    } catch (error) {
      console.error("Error updating security item:", error);
      toast({
        title: "Error",
        description: "Failed to update verification status.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Open the confirmation drawer
  const handleStartVerification = () => {
    setIsDrawerOpen(true);
  };

  const handleVerification = async () => {
    setLoading(true);
    setIsDrawerOpen(false);

    if (!guestIdSecurityItemId || !booking?.id) {
      setLoading(false);
      return;
    }

    try {
      localStorage.setItem(
        "onboardingState",
        JSON.stringify({ guest, booking, room, storedStripeId, manager, accessTimes })
      );

      //{ "autohostId" : { "S" : "AZq74LSj39jmbjAxHtFG" } }

      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();

      const activeManager = manager || storedState?.manager;
      const hasOwnerSecretArn = activeManager?.accountMetas?.superService?.secretArn;
      const hasRoomAutohostId = room?.accountMetas?.autohostId || storedState?.room?.accountMetas?.autohostId;
      const useExtendedVerification = hasOwnerSecretArn && hasRoomAutohostId;

      const endpoint = useExtendedVerification
        ? "https://api.linkbase.ca/security/autohost/extended-guest-verification"
        : "https://api.linkbase.ca/security/autohost/create-reservation";

      console.log("🔐 [ID Verification] Creating verification using endpoint:", endpoint);
      console.log("🔐 [ID Verification] Extended verification enabled:", useExtendedVerification);

      const payload = { booking_id: booking.id };

      const response = await axios.post(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_LINKBASE_API_KEY,
        },
      });

      if (response.data.success && (response.data.data.portalUrl || response.data.data.guestPortalUrl)) {
        const portalUrl = response.data.data.portalUrl || response.data.data.guestPortalUrl;
        const reservationId = response.data.data.reservationId;
        const embedUrl = `https://verifyhub.org/${reservationId}?embed=1`;
        
        console.log("✅ [ID Verification] Verification created successfully");
        console.log("🔗 [ID Verification] Embed URL:", embedUrl);
        console.log("🔗 [ID Verification] Portal URL:", portalUrl);
        console.log("🆔 [ID Verification] Reservation ID:", reservationId);
        
        setVerificationData({
          ...response.data.data,
          embedUrl,
          portalUrl
        });
        
        localStorage.setItem("autohostReservationId", reservationId);
        setShowIframe(true);
      } else {
        throw new Error("Invalid response from verification service");
      }
    } catch (error) {
      console.error("Verification request failed:", error);
      toast({
        title: "Error",
        description: "Failed to initiate verification process.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Lock scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isDrawerOpen]);


  return (
    <Box p={0} m={0} h="100vh" w="100vw" overflow="hidden" bg="#1A202C">
      {/* Header */}
      <Flex alignItems="center" justifyContent="space-between"  p={4}>
        <Flex alignItems="center">
          <IconButton
            icon={<Icon as={FaArrowLeft} />}
            variant="ghost"
            colorScheme="blue"
            bg="#2c3b4b"
            mr={3}
            onClick={async () => {
              if (showIframe) {
                // Check verification status before closing iframe
                await checkVerificationStatus();
                setShowIframe(false);
              } else {
                navigate("/onboarding", {
                  state: { guest, booking, room, storedStripeId, manager, accessTimes, needsRefresh: true },
                  replace: true,
                });
              }
            }}
            aria-label="Back"
          />
          <Text fontWeight="bold" fontSize="16px" color="white">
            {showIframe ? "Verification Portal" : "ID Verification"}
          </Text>
        </Flex>
      </Flex>

      {/* Iframe Display - position:fixed so it breaks out of parent overflow:hidden,
          and uses dvh so iOS browser chrome doesn't clip the bottom of the content */}
      {showIframe && verificationData && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={200}
          bg="#1A202C"
          display="flex"
          flexDirection="column"
          style={{
            /* Respect notch / status bar / browser chrome on all sides */
            paddingTop: 'env(safe-area-inset-top)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
          }}
        >
          {/* Header */}
          <Flex
            alignItems="center"
            p={4}
            flexShrink={0}
            borderBottom="1px solid"
            borderColor="rgba(255,255,255,0.1)"
          >
            <IconButton
              icon={<Icon as={FaArrowLeft} />}
              variant="ghost"
              colorScheme="blue"
              bg="#2c3b4b"
              mr={3}
              onClick={async () => {
                await checkVerificationStatus();
                setShowIframe(false);
              }}
              aria-label="Back"
            />
            <Text fontWeight="bold" fontSize="16px" color="white">
              Verification Portal
            </Text>
          </Flex>

          {/* Scrollable iframe wrapper.
              - flex:1 fills everything below the header inside the fixed overlay
              - overflow:auto + -webkit-overflow-scrolling:touch enables native
                iOS momentum scroll so taller AutoHost screens (e.g. background
                check) can be scrolled to reveal the Continue button
              - paddingBottom:env(safe-area-inset-bottom) keeps content above
                the iPhone home indicator / bottom browser tab bar */}
          <Box
            flex="1"
            overflow="auto"
            style={{
              WebkitOverflowScrolling: 'touch',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            <iframe
              src={verificationData.embedUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block',
              }}
              title="Verification Portal"
              allow="camera; microphone; geolocation; fullscreen"
            />
          </Box>
        </Box>
      )}

      {!showIframe && (
        /* Main Content */
        <VStack spacing={6} align="stretch" maxW="400px" mx="auto" px={4}>
        {/* Checking Status Indicator */}
        {checkingStatus && (
          <Box textAlign="center" py={4}>
            <VStack spacing={3}>
              <Spinner size="lg" color="#817EFF" thickness="4px" />
              <Text fontSize="14px" color="#A0AEC0" fontWeight="500">
                Checking verification status...
              </Text>
            </VStack>
          </Box>
        )}
        
        {/* Security Icon & Title */}
        <VStack spacing={4} pt={6}>
          <Circle size="80px" bg="#2D3748" border="2px solid" borderColor="#4A5568">
            <Icon as={FaShieldAlt} fontSize="32px" color="#817EFF" />
          </Circle>
          
          <VStack spacing={2}>
            <Text fontWeight={800} fontSize={"18px"} textAlign="center" color="white">
              Secure Verification
            </Text>
            <Text fontSize={"14px"} textAlign="center" color="#A0AEC0" lineHeight="1.5">
              Your government ID is needed for this step to ensure security and compliance
            </Text>
          </VStack>
        </VStack>

        <Divider borderColor="#4A5568" />

        {/* Verification Steps Preview */}
        <Box bg="#2D3748" p={4} borderRadius="12px" border="1px solid" borderColor="#4A5568">
          <Text fontSize="14px" fontWeight="600" mb={3} color="white">
            What to expect:
          </Text>
          <VStack spacing={3} align="start">
            <HStack spacing={3}>
              <Circle size="24px" bg="#817EFF" color="white">
                <Text fontSize="12px" fontWeight="bold">1</Text>
              </Circle>
              <Text fontSize="13px" color="#A0AEC0">Upload your government-issued photo ID</Text>
            </HStack>
            <HStack spacing={3}>
              <Circle size="24px" bg="#817EFF" color="white">
                <Text fontSize="12px" fontWeight="bold">2</Text>
              </Circle>
              <Text fontSize="13px" color="#A0AEC0">Take a quick selfie for verification</Text>
            </HStack>
            <HStack spacing={3}>
              <Circle size="24px" bg="#817EFF" color="white">
                <Text fontSize="12px" fontWeight="bold">3</Text>
              </Circle>
              <Text fontSize="13px" color="#A0AEC0">Instant verification (2-3 minutes)</Text>
            </HStack>
          </VStack>
        </Box>

        {/* Verification Button or Success State */}
        {!isVerified ? (
          <Button
            color="white"
            bg={"#817EFF"}
            _hover={{ backgroundColor: "#504CFF", transform: "translateY(-1px)" }}
            _active={{ transform: "translateY(0)" }}
            w={"full"}
            h="52px"
            borderRadius="12px"
            fontSize="16px"
            fontWeight="600"
            onClick={handleStartVerification}
            isLoading={loading}
            loadingText="Loading..."
            spinner={<Spinner size="sm" />}
            leftIcon={!loading ? <Icon as={FaIdCard} /> : undefined}
            transition="all 0.2s"
            boxShadow="0 4px 12px rgba(129, 126, 255, 0.3)"
          >
            {loading ? "Preparing Verification..." : "Start Verification"}
          </Button>
        ) : (
          <Alert
            borderRadius={"12px"}
            bg={"green.50"}
            color={"green.800"}
            border="1px solid"
            borderColor="green.200"
            p={4}
          >
            <Circle size="40px" bg="green.100" mr={3}>
              <Icon as={FaCheck} fontSize={"18px"} color={"green.600"} />
            </Circle>
            <Box>
              <AlertTitle fontSize={"16px"} fontWeight="600">
                Verification Complete!
              </AlertTitle>
              <AlertDescription fontSize={"14px"} mt={1} lineHeight={"1.4"}>
                You are now verified! Thank you for your participation. 
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {/* Security Notice */}
        <Box bg="#2D3748" p={3} borderRadius="8px" border="1px solid" borderColor="#4A5568">
          <HStack spacing={2}>
            <Icon as={FaShieldAlt} fontSize="14px" color="#817EFF" />
            <Text fontSize="12px" color="#A0AEC0" fontWeight="500">
              Your information is encrypted and secure
            </Text>
          </HStack>
        </Box>
        </VStack>
      )}

      {/* Floating Action Button - this step is done, so point the guest at
          whatever is still left rather than a bare "Go Back". */}
      {isVerified && (() => {
        const next = nextIncompleteStep(booking, "GUEST_ID");
        return (
          <Box
            position="fixed"
            bottom="24px"
            right="24px"
            zIndex={1000}
          >
            <Button
              color="white"
              bg={"#817EFF"}
              _hover={{ 
                backgroundColor: "#504CFF", 
                transform: "translateY(-2px)",
                boxShadow: "0 8px 20px rgba(129, 126, 255, 0.4)"
              }}
              _active={{ transform: "translateY(0)" }}
              size="lg"
              borderRadius="50px"
              fontSize="16px"
              fontWeight="600"
              onClick={() => {
                const state = { guest, booking, room, storedStripeId, manager, accessTimes };
                if (next) {
                  navigate(next.path, { state, replace: true });
                } else {
                  navigate("/onboarding", {
                    state: { ...state, needsRefresh: true },
                    replace: true,
                  });
                }
              }}
              leftIcon={next ? undefined : <Icon as={FaArrowLeft} />}
              rightIcon={next ? <Icon as={FaArrowRight} /> : undefined}
              transition="all 0.3s ease"
              boxShadow="0 6px 16px rgba(129, 126, 255, 0.3)"
              px={6}
              py={4}
              h="auto"
              minH="56px"
            >
              {next ? `Continue to ${next.label}` : "Back to Checklist"}
            </Button>
          </Box>
        );
      })()}

      {/* Verification Confirmation Drawer */}
      <AnimatePresence>
        {isDrawerOpen && (
          <>
            {/* Dimmed Background Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Box
                position="fixed"
                top="0"
                left="0"
                right="0"
                bottom="0"
                bg="rgba(0, 0, 0, 0.6)"
                zIndex="1400"
                onClick={() => setIsDrawerOpen(false)}
              />
            </motion.div>
            
            {/* Custom Drawer Content */}
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              style={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                height: "auto",
                maxHeight: "84vh",
                backgroundColor: "#1A202C",
                zIndex: 1500,
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Sticky Header */}
              <Box
                position="sticky"
                top={0}
                bg="#1A202C"
                zIndex={1}
                py={2}
                borderBottom="1px solid"
                borderColor="rgba(255, 255, 255, 0.1)"
                flexShrink={0}
                pl="20px"
                pr={"5px"}
              >
                <Flex alignItems="center" justifyContent="space-between">
                  <Flex alignItems="center" gap={2}>
                    <FaShieldAlt color="#817EFF" />
                    <Text fontSize="16px" fontWeight="700" lineHeight="1.2" color="white">
                      Verification Required
                    </Text>
                  </Flex>
                  <IconButton
                    icon={<FaTimes />}
                    variant="ghost"
                    color="white"
                    onClick={() => setIsDrawerOpen(false)}
                    aria-label="Close"
                    size="lg"
                  />
                </Flex>
              </Box>

              {/* Scrollable Content */}
              <Box flex="1" overflowY="auto" px="20px" py={'20px'}>
                {/* Primary Guest Info Card */}
                <Box
                  bg="rgba(129, 126, 255, 0.1)"
                  border="2px solid"
                  borderColor="#817EFF"
                  borderRadius="12px"
                  p={4}
                  mb={4}
                >
                  <Flex alignItems="center" gap={3} mb={3}>
                    <Circle size="48px" bg="#817EFF" color="white">
                      <Icon as={FaUser} fontSize="20px" />
                    </Circle>
                    <Box flex="1">
                      <Text fontSize="12px" color="#A0AEC0" fontWeight="500" mb={1}>
                        Primary Guest
                      </Text>
                      <Text fontSize="18px" fontWeight="700" color="white">
                        {booking?.guest?.name || guest?.name || "Guest"}
                      </Text>
                      {(booking?.guest?.email || guest?.email) && (
                        <Text fontSize="13px" color="#A0AEC0" mt={1}>
                          {booking?.guest?.email || guest?.email}
                        </Text>
                      )}
                    </Box>
                  </Flex>
                </Box>

                {/* Important Notice */}
                <Box
                  bg="#2D3748"
                  border="1px solid"
                  borderColor="#4A5568"
                  borderRadius="12px"
                  p={4}
                  mb={4}
                >
                  <HStack spacing={2} mb={3}>
                    <Icon as={IoIosAlert} fontSize="20px" color="#FEB2B2" />
                    <Text fontSize="15px" fontWeight="700" color="white">
                      Important Information
                    </Text>
                  </HStack>
                  <Text fontSize="14px" color="#A0AEC0" lineHeight="1.6" mb={3}>
                    The <strong style={{ color: 'white', fontWeight: '700' }}>primary guest</strong> listed above must complete this verification process using their government-issued photo ID.
                  </Text>
                  <Text fontSize="14px" color="#A0AEC0" lineHeight="1.6">
                    This is a security requirement to ensure the safety of all guests and comply with local regulations.
                  </Text>
                </Box>

                {/* What You'll Need */}
                <Box
                  bg="rgba(255, 255, 255, 0.05)"
                  border="1px solid"
                  borderColor="rgba(255, 255, 255, 0.1)"
                  borderRadius="12px"
                  p={4}
                  mb={5}
                >
                  <Text fontSize="15px" fontWeight="700" color="white" mb={3}>
                    What You'll Need:
                  </Text>
                  <VStack spacing={2} align="start">
                    <HStack spacing={3}>
                      <Circle size="8px" bg="#817EFF" />
                      <Text fontSize="14px" color="#A0AEC0">Government-issued photo ID (Passport, Driver's License, etc.)</Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Circle size="8px" bg="#817EFF" />
                      <Text fontSize="14px" color="#A0AEC0">Device with camera for selfie verification</Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Circle size="8px" bg="#817EFF" />
                      <Text fontSize="14px" color="#A0AEC0">2-3 minutes to complete the process</Text>
                    </HStack>
                  </VStack>
                </Box>

                {/* Confirmation Checkbox */}
                <Box
                  bg="#2D3748"
                  border="1px solid"
                  borderColor="#4A5568"
                  borderRadius="12px"
                  p={4}
                  mb={4}
                >
                  <Checkbox
                    size="lg"
                    colorScheme="purple"
                    isChecked={hasConfirmed}
                    onChange={(e) => setHasConfirmed(e.target.checked)}
                    sx={{
                      '& .chakra-checkbox__control': {
                        borderColor: '#817EFF',
                        bg: 'transparent',
                        _checked: {
                          bg: '#817EFF',
                          borderColor: '#817EFF',
                        },
                      },
                    }}
                  >
                    <Text fontSize="14px" color="white" fontWeight="500" ml={2}>
                      I confirm that <strong style={{ color: '#817EFF' }}>{booking?.guest?.name || guest?.name || "the primary guest"}</strong> will complete this verification
                    </Text>
                  </Checkbox>
                </Box>

                {/* Continue Button */}
                <Button
                  color="white"
                  bg={"#817EFF"}
                  _hover={{ 
                    backgroundColor: hasConfirmed ? "#504CFF" : "#817EFF", 
                    transform: hasConfirmed ? "translateY(-1px)" : "none" 
                  }}
                  _active={{ transform: "translateY(0)" }}
                  w={"full"}
                  h="52px"
                  borderRadius="12px"
                  fontSize="16px"
                  fontWeight="600"
                  onClick={handleVerification}
                  isDisabled={!hasConfirmed}
                  isLoading={loading}
                  loadingText="Preparing..."
                  spinner={<Spinner size="sm" />}
                  rightIcon={!loading ? <Icon as={FaArrowRight} /> : undefined}
                  transition="all 0.2s"
                  boxShadow={hasConfirmed ? "0 4px 12px rgba(129, 126, 255, 0.3)" : "none"}
                  opacity={hasConfirmed ? 1 : 0.5}
                  cursor={hasConfirmed ? "pointer" : "not-allowed"}
                >
                  Continue to Verification
                </Button>
              </Box>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Processing Modal - Blocks navigation during verification processing */}
      <Modal 
        isOpen={pollState.showModal} 
        onClose={() => {}} 
        closeOnOverlayClick={false}
        closeOnEsc={false}
        isCentered
      >
        <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(10px)" />
        <ModalContent bg="#1a202c" borderRadius="20px" mx={4}>
          <ModalBody py={8}>
            <VStack spacing={6}>
              {/* Simple spinner - only show when processing */}
              {!pollState.result && (
                <Spinner
                  thickness="4px"
                  speed="0.8s"
                  emptyColor="gray.600"
                  color="#817efc"
                  size="xl"
                />
              )}

              {/* Success icon */}
              {pollState.result?.action === "approved" && (
                <Circle size="64px" bg="green.500">
                  <Icon as={FaCheck} color="white" boxSize={6} />
                </Circle>
              )}

              {/* Rejected icon */}
              {pollState.result?.action === "rejected" && (
                <Circle size="64px" bg="red.500">
                  <Icon as={FaTimes} color="white" boxSize={6} />
                </Circle>
              )}

              {/* Flagged icon */}
              {pollState.result?.action === "flagged" && (
                <Circle size="64px" bg="yellow.500">
                  <Icon as={FaShieldAlt} color="white" boxSize={6} />
                </Circle>
              )}

              {/* Messages based on state */}
              <VStack spacing={3}>
                {/* Processing state */}
                {!pollState.result && (
                  <>
                    <Text fontSize="20px" fontWeight="800" color="white" textAlign="center">
                      Processing Verification
                    </Text>
                    <Text fontSize="14px" fontWeight="500" color="gray.400" textAlign="center" px={4}>
                      Please wait while we finalize your verification. Do not close this window.
                    </Text>
                    {/* Progress indicator - 6 polls total */}
                    {pollState.pollCount > 0 && (
                      <VStack spacing={2} mt={2}>
                        <HStack spacing={2}>
                          {[1, 2, 3, 4, 5, 6].map((i) => (
                            <Circle
                              key={i}
                              size="10px"
                              bg={pollState.pollCount >= i ? "#817efc" : "gray.600"}
                              transition="background 0.3s"
                            />
                          ))}
                        </HStack>
                        <Text fontSize="12px" color="gray.500">
                          Verifying... ({pollState.pollCount}/6)
                        </Text>
                      </VStack>
                    )}
                  </>
                )}

                {/* Approved state */}
                {pollState.result?.action === "approved" && (
                  <>
                    <Text fontSize="20px" fontWeight="800" color="white" textAlign="center">
                      Verification Successful!
                    </Text>
                    <Text fontSize="14px" fontWeight="500" color="gray.400" textAlign="center" px={4}>
                      Your identity has been verified. You're all set to continue.
                    </Text>
                  </>
                )}

                {/* Rejected state */}
                {pollState.result?.action === "rejected" && (
                  <>
                    <Text fontSize="20px" fontWeight="800" color="white" textAlign="center">
                      Verification Unsuccessful
                    </Text>
                    <Text fontSize="14px" fontWeight="500" color="gray.400" textAlign="center" px={4}>
                      We couldn't verify your identity. This may happen due to:
                    </Text>
                    <VStack spacing={1} align="start" px={4}>
                      <Text fontSize="13px" color="gray.500">• Name doesn't match the reservation</Text>
                      <Text fontSize="13px" color="gray.500">• ID photo is unclear or expired</Text>
                      <Text fontSize="13px" color="gray.500">• Selfie doesn't match the ID photo</Text>
                      <Text fontSize="13px" color="gray.500">• Document type not accepted</Text>
                    </VStack>
                    <Text fontSize="13px" fontWeight="500" color="gray.400" textAlign="center" px={4} mt={2}>
                      Please try again with a valid government-issued ID.
                    </Text>
                  </>
                )}

                {/* Flagged state */}
                {pollState.result?.action === "flagged" && (
                  <>
                    <Text fontSize="20px" fontWeight="800" color="white" textAlign="center">
                      Verification Under Review
                    </Text>
                    <Text fontSize="14px" fontWeight="500" color="gray.400" textAlign="center" px={4}>
                      Your verification requires additional review. The host will be in touch if more information is needed.
                    </Text>
                  </>
                )}
              </VStack>
            </VStack>
          </ModalBody>

          {/* Footer with action button */}
          {pollState.result && (
            <ModalFooter>
              <Button
                w="full"
                bg={pollState.result.action === "rejected" ? "red.500" : "#817efc"}
                color="white"
                h="48px"
                borderRadius="12px"
                fontSize="16px"
                fontWeight="600"
                onClick={() => {
                  console.log(`✅ [Polling] User clicked ${pollState.result.action === "rejected" ? "Try Again" : "Continue"}`);
                  setPollState(prev => ({ ...prev, showModal: false }));
                  // Rejected → stay right here so the guest retries the
                  // verification; navigating history could land on another
                  // step screen. Anything else means this step is settled:
                  // go to the next pending step, or home to the checklist —
                  // never navigate(-1), which depends on how they got here.
                  if (pollState.result.action === "rejected") {
                    return;
                  }
                  const state = { guest, booking, room, storedStripeId, manager, accessTimes };
                  const next = nextIncompleteStep(booking, "GUEST_ID");
                  if (next) {
                    navigate(next.path, { state, replace: true });
                  } else {
                    navigate("/onboarding", {
                      state: { ...state, needsRefresh: true },
                      replace: true,
                    });
                  }
                }}
                _hover={{ bg: pollState.result.action === "rejected" ? "red.600" : "#6c60fc" }}
              >
                {pollState.result.action === "rejected" ? "Try Again" : "Continue"}
              </Button>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};
